import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Panel, PanelHeader, PanelHeaderBack, Group, Div, Cell, Button, Spinner,
  FormItem, Input, Text, Headline, ModalRoot,
  ModalPageHeader, IconButton, Checkbox, Snackbar, Select, Card, Progress,
} from '@vkontakte/vkui';
import { Icon24Add, Icon24Delete, Icon24Write, Icon24Cancel } from '@vkontakte/icons';
import { api } from '../../api/client';
import { useWeddingStore } from '../../stores/wedding';
import type { Expense, Payment, ExpenseCategory, BudgetSummary } from '../../types';
import { DragHandle } from '../../ui/DragHandle';
import { ResizableModalPage, sheetId } from '../../ui/ResizableModalPage';

type ExpenseForm = {
  name: string;
  amount: string;
  note: string;
  category_id: string;
};

type PaymentForm = {
  name: string;
  amount: string;
  due_date: string;
  note: string;
};

const emptyExpense: ExpenseForm = { name: '', amount: '', note: '', category_id: '' };

// default_category icons 1..11
const CAT_ICONS: Record<number, string> = {
  1: '👗',
  2: '💄',
  3: '🎵',
  4: '💐',
  5: '🎀',
  6: '💍',
  7: '📷',
  8: '💒',
  9: '🍽️',
  10: '🚗',
  11: '🏠',
};
function catIcon(iconId?: number | null) {
  if (iconId == null) return '📁';
  return CAT_ICONS[iconId] || '📁';
}
const emptyPayment: PaymentForm = { name: '', amount: '', due_date: '', note: '' };

function fmt(n: number | null | undefined, currency = 'RUB'): string {
  if (n == null) return '—';
  const sym = currency === 'RUB' ? '₽' : currency;
  return `${n.toLocaleString('ru-RU')} ${sym}`;
}

export default function BudgetPage() {
  const navigate = useNavigate();
  const { currentWedding, weddingsLoaded } = useWeddingStore();

  const [summary, setSummary] = useState<BudgetSummary | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [payments, setPayments] = useState<Record<string, Payment[]>>({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<'expense' | 'payment' | 'categories' | null>(null);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [paymentExpense, setPaymentExpense] = useState<Expense | null>(null);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [expenseForm, setExpenseForm] = useState<ExpenseForm>(emptyExpense);
  const [paymentForm, setPaymentForm] = useState<PaymentForm>(emptyPayment);
  const [newCat, setNewCat] = useState('');
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);
  const [openToken, setOpenToken] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState<Expense | null>(null);
  // drill-down: open category → header with its name + only its expenses
  const [openCat, setOpenCat] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    if (!currentWedding) return;
    setLoading(true);
    try {
      const q = openCat ? `?category_id=${openCat}` : '';
      const [sum, expRes, cats] = await Promise.all([
        api.get<BudgetSummary>(`weddings/${currentWedding.id}/expenses/summary`),
        api.get<{ items: Expense[]; total: number }>(`weddings/${currentWedding.id}/expenses${q}`),
        api.get<ExpenseCategory[]>(`weddings/${currentWedding.id}/expenses/categories`),
      ]);
      setSummary(sum);
      setExpenses(expRes.items);
      setCategories(cats);
      // load payments per expense
      const map: Record<string, Payment[]> = {};
      await Promise.all(expRes.items.map(async (e) => {
        try {
          map[e.id] = await api.get<Payment[]>(`weddings/${currentWedding.id}/expenses/${e.id}/payments`);
        } catch {
          map[e.id] = [];
        }
      }));
      setPayments(map);
    } catch {
      setSnack('Ошибка загрузки бюджета');
    } finally {
      setLoading(false);
    }
  }, [currentWedding, openCat]);

  useEffect(() => {
    if (!currentWedding) {
      if (weddingsLoaded) navigate('/');
      return;
    }
    void loadAll();
  }, [currentWedding, weddingsLoaded, loadAll, navigate]);

  const catName = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of categories) m.set(c.id, c.name);
    return m;
  }, [categories]);

  const budgetPct = useMemo(() => {
    if (!summary?.budget || summary.budget <= 0) return null;
    return Math.min(100, Math.round((summary.planned / summary.budget) * 100));
  }, [summary]);

  async function saveExpense() {
    if (!currentWedding || !expenseForm.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: expenseForm.name.trim(),
        amount: expenseForm.amount ? Number(expenseForm.amount) : undefined,
        note: expenseForm.note.trim() || undefined,
        category_id: expenseForm.category_id || undefined,
      };
      if (editingExpense) {
        await api.patch(`weddings/${currentWedding.id}/expenses/${editingExpense.id}`, payload);
        setSnack('Расход обновлён');
      } else {
        await api.post(`weddings/${currentWedding.id}/expenses`, payload);
        setSnack('Расход добавлен');
      }
      setOpenToken((t) => t + 1);
      setModal(null);
      await loadAll();
    } catch {
      setSnack('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  }

  async function savePayment() {
    if (!currentWedding || !paymentExpense || !paymentForm.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: paymentForm.name.trim(),
        amount: paymentForm.amount ? Number(paymentForm.amount) : undefined,
        due_date: paymentForm.due_date || undefined,
        note: paymentForm.note.trim() || undefined,
      };
      if (editingPayment) {
        await api.patch(
          `weddings/${currentWedding.id}/expenses/${paymentExpense.id}/payments/${editingPayment.id}`,
          payload,
        );
        setSnack('Платёж обновлён');
      } else {
        await api.post(`weddings/${currentWedding.id}/expenses/${paymentExpense.id}/payments`, payload);
        setSnack('Платёж добавлен');
      }
      setOpenToken((t) => t + 1);
      setModal(null);
      await loadAll();
    } catch {
      setSnack('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  }

  async function togglePaid(expenseId: string, p: Payment) {
    if (!currentWedding) return;
    try {
      await api.patch(
        `weddings/${currentWedding.id}/expenses/${expenseId}/payments/${p.id}`,
        { is_paid: !p.is_paid },
      );
      await loadAll();
    } catch {
      setSnack('Ошибка');
    }
  }

  async function addCategory() {
    if (!currentWedding || !newCat.trim()) return;
    try {
      await api.post(`weddings/${currentWedding.id}/expenses/categories`, { name: newCat.trim() });
      setNewCat('');
      await loadAll();
      setSnack('Категория создана');
    } catch {
      setSnack('Не удалось создать');
    }
  }

  return (
    <Panel>
      <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/')} />}>
        Бюджет
      </PanelHeader>

      {summary && (
        <Group>
          <Card style={{ margin: 12, padding: 16 }}>
            <Headline level="2">
              {fmt(summary.planned, summary.currency)} / {fmt(summary.budget, summary.currency)}
            </Headline>
            {budgetPct != null && (
              <>
                <Text style={{ marginTop: 6, color: 'var(--vkui--color_text_secondary)', fontSize: 13 }}>
                  Потрачено {fmt(summary.planned, summary.currency)} из {fmt(summary.budget, summary.currency)} ({budgetPct}%)
                </Text>
                <Progress value={budgetPct} style={{ marginTop: 10 }} />
              </>
            )}
            <Div style={{ display: 'flex', gap: 12, padding: '12px 0 0' }}>
              <div style={{ flex: 1 }}>
                <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>Оплачено</Text>
                <Headline level="2">{fmt(summary.paid, summary.currency)}</Headline>
              </div>
              <div style={{ flex: 1 }}>
                <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>К оплате</Text>
                <Headline level="2">{fmt(summary.unpaid, summary.currency)}</Headline>
              </div>
              <div style={{ flex: 1 }}>
                <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>Остаток</Text>
                <Headline level="2">
                  {summary.remaining != null
                    ? fmt(summary.remaining, summary.currency)
                    : '—'}
                </Headline>
              </div>
            </Div>
            {summary.remaining != null && summary.remaining < 0 && (
              <Text style={{ marginTop: 8, color: 'var(--vkui--color_text_negative)' }}>
                ⚠️ Превышение бюджета
              </Text>
            )}
          </Card>
        </Group>
      )}

      {/* Categories carousel under summary — unchanged UI */}
      {categories.length > 0 && (
        <Group header={<Text>Категории</Text>}>
          <div
            style={{
              display: 'flex',
              gap: 8,
              overflowX: 'auto',
              padding: '4px 12px 14px',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
            }}
          >
            <Card
              onClick={() => setOpenCat(null)}
              style={{
                minWidth: 72, padding: '12px 8px', textAlign: 'center', cursor: 'pointer',
                background: openCat == null ? 'var(--vkui--color_background_accent)' : 'var(--vkui--color_background_secondary)',
                color: openCat == null ? '#fff' : undefined,
              }}
            >
              <div style={{ fontSize: 22 }}>📁</div>
              <Text style={{ fontSize: 11, marginTop: 4, color: openCat == null ? '#fff' : 'var(--vkui--color_text_primary)' }}>
                Все
              </Text>
            </Card>
            {categories.map((c) => {
              const active = openCat === c.id;
              return (
                <Card
                  key={c.id}
                  onClick={() => setOpenCat(active ? null : c.id)}
                  style={{
                    minWidth: 72, padding: '12px 8px', textAlign: 'center', cursor: 'pointer',
                    background: active ? 'var(--vkui--color_background_accent)' : 'var(--vkui--color_background_secondary)',
                  }}
                >
                  <div style={{ fontSize: 22 }}>{catIcon(c.icon_id)}</div>
                  <Text style={{
                    fontSize: 11, marginTop: 4, lineHeight: 1.2,
                    color: active ? '#fff' : 'var(--vkui--color_text_primary)',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {c.name}
                  </Text>
                </Card>
              );
            })}
          </div>
        </Group>
      )}


      <Group>
        <Div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            size="l"
            mode="primary"
            before={<Icon24Add />}
            onClick={() => {
              setEditingExpense(null);
              setExpenseForm({ ...emptyExpense, category_id: openCat || '' });
              setOpenToken((t) => t + 1);
              setModal('expense');
            }}
          >
            Расход
          </Button>
          <Button size="l" mode="secondary" onClick={() => setModal('categories')}>
            Категории
          </Button>
        </Div>
      </Group>

      {loading ? (
        <Group>
          <Div style={{ textAlign: 'center', padding: 40 }}><Spinner size="l" /></Div>
        </Group>
      ) : expenses.length === 0 ? (
        <Group>
          <Div style={{ textAlign: 'center', padding: 32 }}>
            <Headline level="2">{openCat ? 'В этой категории нет расходов' : 'Нет расходов'}</Headline>
            <Text style={{ marginTop: 8, color: 'var(--vkui--color_text_secondary)' }}>
              {openCat ? 'Добавьте расход в эту категорию' : 'Добавьте первый расход'}
            </Text>
          </Div>
        </Group>
      ) : (
        <Group header={<Text>{openCat ? `Расходы · ${expenses.length}` : `Расходы: ${expenses.length}`}</Text>}>
          {(() => {
            // drill-down: one category — flat list under its name
            // all: group by category, each group titled with category name
            const groups: { key: string; title: string; items: Expense[] }[] = [];
            for (const e of expenses) {
              const key = e.category_id || '';
              const title = e.category_id ? (catName.get(e.category_id) || 'Без категории') : 'Без категории';
              const g = groups.find((x) => x.key === key);
              if (g) g.items.push(e);
              else groups.push({ key, title, items: [e] });
            }
            groups.sort((a, b) => (a.key === '' ? 1 : b.key === '' ? -1 : a.title.localeCompare(b.title)));

            return groups.map((g) => (
              <Card
                key={g.key || 'none'}
                style={{
                  margin: '8px 12px',
                  padding: 0,
                  borderRadius: 12,
                  background: 'var(--vkui--color_background_content)',
                  border: '1px solid var(--vkui--color_separator_primary)',
                  overflow: 'hidden',
                }}
              >
                <div style={{
                  padding: '12px 14px 8px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--vkui--color_text_secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  borderBottom: '1px solid var(--vkui--color_separator_primary)',
                }}>
                  {g.title}
                </div>

                {g.items.map((e, idx) => {
                  const pays = payments[e.id] || [];
                  const paidSum = pays.filter((x) => x.is_paid).reduce((s, x) => s + (Number(x.amount) || 0), 0);
                  const planSum = pays.reduce((s, x) => s + (Number(x.amount) || 0), 0);
                  const status = pays.length === 0
                    ? 'без платежей'
                    : planSum > 0 && paidSum >= planSum
                      ? 'оплачено'
                      : paidSum > 0
                        ? 'частично'
                        : 'к оплате';

                  return (
                    <div
                      key={e.id}
                      style={{
                        padding: '12px 14px',
                        borderTop: idx === 0 ? undefined : '1px solid var(--vkui--color_separator_primary)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>{e.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)', marginTop: 4 }}>
                            {e.note || '—'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <div style={{ fontSize: 15, fontWeight: 700 }}>{fmt(e.amount, summary?.currency)}</div>
                          <div style={{ fontSize: 11, color: 'var(--vkui--color_text_secondary)', marginTop: 2 }}>
                            оплачено {fmt(paidSum, summary?.currency)}
                          </div>
                        </div>
                      </div>

                      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--vkui--color_text_secondary)' }}>
                        Статус: {status}
                        {pays.length > 0 ? ` · платежи ${pays.filter((x) => x.is_paid).length}/${pays.length}` : ''}
                      </div>

                      <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                        <Button size="s" mode="secondary" style={{ flex: 1, minWidth: 0, padding: '6px 4px', whiteSpace: 'nowrap' }} onClick={() => {
                          setOpenToken((t) => t + 1);
                          setPaymentExpense(e);
                          setEditingPayment(null);
                          setPaymentForm(emptyPayment);
                          setModal('payment');
                        }}>+ Платёж</Button>
                        <Button size="s" mode="secondary" style={{ flex: 1, minWidth: 0, padding: '6px 4px', whiteSpace: 'nowrap' }} onClick={() => {
                          setEditingExpense(e);
                          setExpenseForm({
                            name: e.name,
                            amount: e.amount != null ? String(e.amount) : '',
                            note: e.note || '',
                            category_id: e.category_id || '',
                          });
                          setOpenToken((t) => t + 1);
                          setModal('expense');
                        }}>Изменить</Button>
                        <Button size="s" mode="secondary" appearance="negative" style={{ flex: 1, minWidth: 0, padding: '6px 4px', whiteSpace: 'nowrap' }} onClick={() => setConfirmDelete(e)}>Удалить</Button>
                      </div>

                      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {pays.length === 0 && (
                          <div style={{
                            fontSize: 12, color: 'var(--vkui--color_text_secondary)',
                            padding: '8px 10px', borderRadius: 8,
                            background: 'var(--vkui--color_background_secondary)',
                          }}>
                            Платежей нет
                          </div>
                        )}
                        {pays.map((pay) => (
                          <div key={pay.id} style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '8px 10px', borderRadius: 8,
                            background: 'var(--vkui--color_background_secondary)',
                          }}>
                            <Checkbox
                              checked={pay.is_paid}
                              onChange={() => void togglePaid(e.id, pay)}
                              aria-label="Оплачено"
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{
                                fontSize: 13,
                                textDecoration: pay.is_paid ? 'line-through' : undefined,
                                opacity: pay.is_paid ? 0.65 : 1,
                              }}>{pay.name}</div>
                              <div style={{ fontSize: 11, color: 'var(--vkui--color_text_secondary)', marginTop: 2 }}>
                                {pay.due_date ? `до ${new Date(pay.due_date).toLocaleDateString('ru-RU')} · ` : ''}
                                {pay.is_paid ? 'Оплачено' : 'К оплате'}
                              </div>
                            </div>
                            <strong style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmt(pay.amount, summary?.currency)}</strong>
                            <IconButton label="Изменить платёж" onClick={() => {
                              setOpenToken((t) => t + 1);
                              setPaymentExpense(e);
                              setEditingPayment(pay);
                              setPaymentForm({
                                name: pay.name,
                                amount: pay.amount != null ? String(pay.amount) : '',
                                due_date: pay.due_date || '',
                                note: pay.note || '',
                              });
                              setModal('payment');
                            }}>
                              <Icon24Write />
                            </IconButton>
                            <IconButton label="Удалить платёж" onClick={async () => {
                              try {
                                await api.delete(`weddings/${currentWedding!.id}/expenses/${e.id}/payments/${pay.id}`);
                                await loadAll();
                              } catch {
                                setSnack('Ошибка удаления');
                              }
                            }}>
                              <Icon24Delete />
                            </IconButton>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </Card>
            ));
          })()}
        </Group>
      )}

      <ModalRoot
        activeModal={
          modal === 'expense' ? sheetId('budget-expense', openToken)
            : modal === 'payment' ? sheetId('budget-payment', openToken)
              : modal === 'categories' ? sheetId('budget-categories', openToken) : null
        }
        onClose={() => setModal(null)}
      >
        <ResizableModalPage openToken={openToken} id="budget-expense" onClose={() => setModal(null)} settlingHeight={80}>
          <ModalPageHeader>{editingExpense ? 'Редактировать расход' : 'Новый расход'}</ModalPageHeader>
        <DragHandle />
          <Group>
            <FormItem top="Название *">
              <Input value={expenseForm.name} onChange={(e) => setExpenseForm({ ...expenseForm, name: e.target.value })} placeholder="Аренда зала" />
            </FormItem>
            <FormItem top={`Сумма (${summary?.currency || 'RUB'})`}>
              <Input
                type="number"
                inputMode="decimal"
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                placeholder="50000"
              />
            </FormItem>
            <FormItem top="Категория">
              <Select
                value={expenseForm.category_id}
                onChange={(e) => setExpenseForm({ ...expenseForm, category_id: e.target.value })}
                options={[
                  { label: 'Без категории', value: '' },
                  ...categories.map((c) => ({ label: c.name, value: c.id })),
                ]}
              />
            </FormItem>
            <FormItem top="Заметка">
              <Input value={expenseForm.note} onChange={(e) => setExpenseForm({ ...expenseForm, note: e.target.value })} />
            </FormItem>
            <Div>
              <Button size="l" mode="primary" stretched onClick={saveExpense} disabled={saving || !expenseForm.name.trim()}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </Button>
              <Button size="l" mode="secondary" stretched style={{ marginTop: 8 }} onClick={() => setModal(null)}>
                Отмена
              </Button>
            </Div>
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="budget-payment" onClose={() => setModal(null)} settlingHeight={80}>
          <ModalPageHeader>
            {editingPayment ? 'Редактировать платёж' : `Платёж: ${paymentExpense?.name || ''}`}
          </ModalPageHeader>
        <DragHandle />
          <Group>
            <FormItem top="Описание *">
              <Input value={paymentForm.name} onChange={(e) => setPaymentForm({ ...paymentForm, name: e.target.value })} placeholder="Предоплата 50%" />
            </FormItem>
            <FormItem top="Сумма">
              <Input
                type="number"
                inputMode="decimal"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
              />
            </FormItem>
            <FormItem top="Дата оплаты">
              <Input type="date" value={paymentForm.due_date} onChange={(e) => setPaymentForm({ ...paymentForm, due_date: e.target.value })} />
            </FormItem>
            <FormItem top="Заметка">
              <Input value={paymentForm.note} onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })} />
            </FormItem>
            <Div>
              <Button size="l" mode="primary" stretched onClick={savePayment} disabled={saving || !paymentForm.name.trim()}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </Button>
              <Button size="l" mode="secondary" stretched style={{ marginTop: 8 }} onClick={() => setModal(null)}>
                Отмена
              </Button>
            </Div>
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="budget-categories" onClose={() => setModal(null)} settlingHeight={80}>
          <ModalPageHeader>Категории расходов</ModalPageHeader>
        <DragHandle />
          <Group>
            <Div style={{ display: 'flex', gap: 8 }}>
              <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Название" style={{ flex: 1 }} />
              <Button onClick={() => void addCategory()} disabled={!newCat.trim()}>+</Button>
            </Div>
            {categories.map((c) => (
              <Cell
                key={c.id}
                subtitle="Только название · без платежей"
                after={
                  <>
                    <IconButton
                      label="Переименовать"
                      onClick={() => {
                        const name = window.prompt('Новое название категории', c.name);
                        if (name && name.trim() && name.trim() !== c.name) {
                          void api
                            .patch(`weddings/${currentWedding!.id}/expenses/categories/${c.id}`, {
                              name: name.trim(),
                            })
                            .then(() => {
                              setSnack('Категория обновлена');
                              return loadAll();
                            })
                            .catch(() => setSnack('Ошибка'));
                        }
                      }}
                    >
                      <Icon24Write />
                    </IconButton>
                    <IconButton
                      label="Удалить"
                      onClick={async () => {
                        try {
                          await api.delete(`weddings/${currentWedding!.id}/expenses/categories/${c.id}`);
                          await loadAll();
                        } catch {
                          setSnack('Ошибка');
                        }
                      }}
                    >
                      <Icon24Delete />
                    </IconButton>
                  </>
                }
              >
                {catIcon(c.icon_id)} {c.name}
              </Cell>
            ))}
            {/* clearance so last category (Жилье) is not hidden under TabBar */}
            <Div style={{ height: 72 }} />
          </Group>
        </ResizableModalPage>
      </ModalRoot>

      {confirmDelete && (
        <Div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Card style={{ padding: 20, width: 280 }}>
            <Headline level="2">Удалить «{confirmDelete.name}»?</Headline>
            <Button
              mode="primary"
              stretched
              style={{ marginTop: 16 }}
              onClick={async () => {
                try {
                  await api.delete(`weddings/${currentWedding!.id}/expenses/${confirmDelete.id}`);
                  await loadAll();
                  setSnack('Расход удалён');
                } catch {
                  setSnack('Ошибка удаления');
                }
                setOpenToken((t) => t + 1);
                setConfirmDelete(null);
              }}
            >
              Удалить
            </Button>
            <Button mode="secondary" stretched style={{ marginTop: 8 }} onClick={() => setConfirmDelete(null)}>
              Отмена
            </Button>
          </Card>
        </Div>
      )}

      {snack && (
        <Snackbar onClose={() => setSnack(null)} onClosed={() => setSnack(null)} before={<Icon24Cancel />}>
          {snack}
        </Snackbar>
      )}
      <Div style={{ height: 24 }} />
    </Panel>
  );
}
